import { useEffect, useState } from 'react';
import { Plus, Check, IndianRupee, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import IncentiveDealClosures from './IncentiveDealClosures.jsx';

/* The page now has three tabs:
 *   • Deal closures (new — Chunk E2): the 2% pool / BDA-BDE-BDM flow
 *   • Payouts       (legacy): ad-hoc per-user incentive payouts
 *   • Rules         (legacy): admin-defined trigger rules
 * The legacy tabs stay because they cover spiffs/bonuses that aren't tied
 * to a closed deal. */
export default function AdminIncentives() {
    const [tab, setTab] = useState('closures');
    return (
        <div className="space-y-4">
            <div>
                <h1 className="text-2xl font-extrabold">Incentives — admin</h1>
                <p className="text-sm text-ink-500">Distribute deal-closure pools and manage ad-hoc payouts.</p>
            </div>
            <div className="flex gap-2 flex-wrap">
                <button className={`ag-tab ${tab==='closures' ? 'ag-tab-active' : 'ag-tab-inactive'}`} onClick={() => setTab('closures')}>
                    <Sparkles className="h-3.5 w-3.5" /> Deal closures
                </button>
                <button className={`ag-tab ${tab==='payouts'  ? 'ag-tab-active' : 'ag-tab-inactive'}`} onClick={() => setTab('payouts')}>Payouts</button>
                <button className={`ag-tab ${tab==='rules'    ? 'ag-tab-active' : 'ag-tab-inactive'}`} onClick={() => setTab('rules')}>Rules</button>
            </div>
            {tab === 'closures' && <IncentiveDealClosures />}
            {tab === 'payouts'  && <Payouts />}
            {tab === 'rules'    && <Rules />}
        </div>
    );
}

function Payouts() {
    const [items, setItems] = useState([]);
    const [users, setUsers] = useState([]);
    const [openNew, setOpenNew] = useState(false);
    const load = () => api.get('/incentives/payouts').then(r => setItems(r.data.items));
    useEffect(() => {
        load();
        api.get('/users', { params: { status: 'active', role: 'sales_user' } }).then(r => setUsers(r.data.items));
    }, []);

    const update = async (p, status) => {
        await api.patch(`/incentives/payouts/${p._id}`, { status });
        toast.success(`Marked ${status}`);
        load();
    };

    return (
        <>
            <div className="flex justify-end">
                <button className="ag-btn-primary" onClick={() => setOpenNew(true)}><Plus className="h-4 w-4" /> Add payout</button>
            </div>
            <div className="ag-card p-0 overflow-x-auto">
                <table className="ag-table">
                    <thead><tr>
                        <th>User</th><th>Month</th><th>Rule</th><th>Amount</th><th>Status</th><th>Note</th><th></th>
                    </tr></thead>
                    <tbody>
                        {items.map(p => (
                            <tr key={p._id}>
                                <td className="font-medium">{p.user?.name}</td>
                                <td>{p.monthKey}</td>
                                <td>{p.rule?.name || '—'}</td>
                                <td>₹ {p.amountINR.toLocaleString('en-IN')}</td>
                                <td><span className={`ag-chip ${
                                    p.status==='paid' ? 'bg-emerald-100 text-emerald-700'
                                    : p.status==='approved' ? 'bg-blue-100 text-blue-700'
                                    : p.status==='cancelled' ? 'bg-rose-100 text-rose-700'
                                    : 'bg-amber-100 text-amber-700'}`}>{p.status}</span>
                                </td>
                                <td className="text-xs text-ink-500">{p.note || '—'}</td>
                                <td>
                                    <div className="flex gap-2 justify-end">
                                        {p.status === 'pending' && <button className="ag-btn-ghost text-xs" onClick={() => update(p, 'approved')}>Approve</button>}
                                        {p.status === 'approved' && <button className="ag-btn-primary text-xs" onClick={() => update(p, 'paid')}><Check className="h-4 w-4" /> Paid</button>}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && <tr><td colSpan={7} className="text-center text-ink-500 py-8">No payouts yet.</td></tr>}
                    </tbody>
                </table>
            </div>
            {openNew && <NewPayoutModal users={users} onClose={(s) => { setOpenNew(false); if (s) load(); }} />}
        </>
    );
}

function NewPayoutModal({ users, onClose }) {
    const monthKey = new Date().toISOString().slice(0, 7);
    const [form, setForm] = useState({ user: users[0]?._id || '', monthKey, amountINR: 1000, note: '', status: 'pending' });
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post('/incentives/payouts', form);
            toast.success('Payout created');
            onClose(true);
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-40 bg-ink-900/40 grid place-items-center p-4" onClick={() => onClose(false)}>
            <form onSubmit={submit} className="bg-white rounded-xl2 shadow-soft w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}>
                <h3 className="font-bold text-lg">New payout</h3>
                <div>
                    <label className="ag-label">User</label>
                    <select className="ag-input" value={form.user} onChange={e => setForm({...form, user: e.target.value})} required>
                        {users.map(u => <option key={u._id} value={u._id}>{u.name} — {u.email}</option>)}
                    </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Month (YYYY-MM)" value={form.monthKey} onChange={v => setForm({...form, monthKey: v})} required />
                    <Field label="Amount ₹" type="number" value={form.amountINR} onChange={v => setForm({...form, amountINR: Number(v)})} required />
                </div>
                <Field label="Note" value={form.note} onChange={v => setForm({...form, note: v})} />
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" className="ag-btn-ghost" onClick={() => onClose(false)}>Cancel</button>
                    <button className="ag-btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Create'}</button>
                </div>
            </form>
        </div>
    );
}

function Rules() {
    const [items, setItems] = useState([]);
    const [openNew, setOpenNew] = useState(false);
    const load = () => api.get('/incentives/rules').then(r => setItems(r.data.items));
    useEffect(() => { load(); }, []);
    return (
        <>
            <div className="flex justify-end">
                <button className="ag-btn-primary" onClick={() => setOpenNew(true)}><Plus className="h-4 w-4" /> New rule</button>
            </div>
            <div className="ag-card p-0 overflow-x-auto">
                <table className="ag-table">
                    <thead><tr><th>Name</th><th>Trigger</th><th>Amount</th><th>Active</th></tr></thead>
                    <tbody>
                        {items.map(r => (
                            <tr key={r._id}>
                                <td className="font-medium">{r.name}</td>
                                <td className="text-xs uppercase tracking-wider text-ink-500">{r.appliesTo}</td>
                                <td>₹ {r.amountINR.toLocaleString('en-IN')}</td>
                                <td>{r.isActive ? '✓' : '—'}</td>
                            </tr>
                        ))}
                        {items.length === 0 && <tr><td colSpan={4} className="text-center text-ink-500 py-8">No rules yet.</td></tr>}
                    </tbody>
                </table>
            </div>
            {openNew && <NewRuleModal onClose={(s) => { setOpenNew(false); if (s) load(); }} />}
        </>
    );
}

function NewRuleModal({ onClose }) {
    const [form, setForm] = useState({ name: '', appliesTo: 'lead_won', amountINR: 500, isActive: true });
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try { await api.post('/incentives/rules', form); toast.success('Rule created'); onClose(true); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-40 bg-ink-900/40 grid place-items-center p-4" onClick={() => onClose(false)}>
            <form onSubmit={submit} className="bg-white rounded-xl2 shadow-soft w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}>
                <h3 className="font-bold text-lg">New incentive rule</h3>
                <Field label="Name *" value={form.name} onChange={v => setForm({...form, name: v})} required />
                <div>
                    <label className="ag-label">Applies to</label>
                    <select className="ag-input" value={form.appliesTo} onChange={e => setForm({...form, appliesTo: e.target.value})}>
                        <option value="lead_won">Lead won</option>
                        <option value="lead_qualified">Lead qualified</option>
                        <option value="booking_completed">Booking completed</option>
                        <option value="program_sold">Program sold</option>
                        <option value="custom">Custom (manual)</option>
                    </select>
                </div>
                <Field label="Amount ₹" type="number" value={form.amountINR} onChange={v => setForm({...form, amountINR: Number(v)})} required />
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.isActive} onChange={e => setForm({...form, isActive: e.target.checked})} /> Active
                </label>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" className="ag-btn-ghost" onClick={() => onClose(false)}>Cancel</button>
                    <button className="ag-btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Create'}</button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, value, onChange, type='text', required }) {
    return (
        <div>
            <label className="ag-label">{label}</label>
            <input className="ag-input" type={type} required={required} value={value} onChange={e => onChange(e.target.value)} />
        </div>
    );
}
